'use client';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { z } from 'zod';
import _ from 'lodash';
import AsyncSelectCreatable from 'react-select/async-creatable';
import { Button } from 'components/ui/button';
import { CloseIcon, SearchIcon } from 'components/icons/v2';
import {
  ControlComponent, ICreatableNewOption,
  MenuComponent,
  OptionWithoutSelectedComponent,
  PlaceholderComponent
} from 'components/select';
import { SetTagInput, TagNode } from 'generated/types';
import {
  useCreateNewHobbyMutation,
  useCreateNewSkillMutation,
  useLoadHobbiesMutation,
  useLoadSkillsMutation,
  useSaveHobbiesMutation,
  useSaveSkillsMutation
} from 'generated/hooks';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useToast } from 'hooks/useToast';
import { LoaderCircle } from 'lucide-react';
import { TabIds } from './ProfileForm';
import { useAdditionalFormSpace } from '../_hooks/useAdditionalFormSpace';
import { UserQuery } from 'generated/operations';
import { AnimatedButton, AnimatedButtonState } from 'components/ui/AnimatedButton';

type EditUserNode = NonNullable<UserQuery['user']>;

interface IServicesForm {
  hobbies: Tag[];
  skills: Tag[];
}

const servicesSchema = z.object({
  hobbies: z.array(z.object({
    id: z.string(),
    name: z.string()
  })),
  skills: z.array(z.object({
    id: z.string(),
    name: z.string()
  }))
});

type Tag = Pick<TagNode, 'name' | 'id'>;

interface IServices {
  currentTab: TabIds;
  user?: EditUserNode | null;
  hobbies: Tag[];
  skills: Tag[];
  onSetDirtyForm: (formName: TabIds, isDirty: boolean) => void;
  onUpdateUser: (user: EditUserNode) => void;
}

export const Services: FC<IServices> = (props) => {
  const {
    currentTab,
    user,
    skills,
    hobbies,
    onSetDirtyForm,
    onUpdateUser,
  } = props;

  const [skillsSaved, setSavedSkills] = useState((user?.tags ?? []).filter(tag => !tag.isHobby).map(tag => ({
    name: tag.name,
    id: tag.id
  })));

  const [hobbiesSaved, setSavedHobbies] = useState((user?.tags ?? []).filter(tag => tag.isHobby).map(tag => ({
    name: tag.name,
    id: tag.id
  })));

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    setValue
  } = useForm<IServicesForm>({
    mode: 'all',
    values: {
      hobbies: [...hobbiesSaved],
      skills: [...skillsSaved]
    },
    resolver: standardSchemaResolver(servicesSchema)
  });

  const checkIsDirty = useCallback((value1: Tag[], value2: Tag[]) => {
    return !_.isEqual(value1, value2);
  }, []);

  const [saveHobbiesSate, saveHobbies] = useSaveHobbiesMutation();
  const [saveSkillsSate, saveSkills] = useSaveSkillsMutation();

  const [, loadHobbies] = useLoadHobbiesMutation();
  const [, loadSkills] = useLoadSkillsMutation();
  const [, createSkill] = useCreateNewSkillMutation();
  const [, createHobby] = useCreateNewHobbyMutation();

  const { handleToast } = useToast();

  const { handleErrors } = useFormErrorHandling();

  const [saveBtnState, setSaveBtnState] = useState<AnimatedButtonState>('idle')

  const skillsWatch = useWatch({
    control,
    name: 'skills'
  });

  const hobbiesWatch = useWatch({
    control,
    name: 'hobbies'
  });

  const onSubmit = useCallback(async (data: IServicesForm) => {
    setSaveBtnState('loading');
    try {
      if (checkIsDirty(data.skills, skillsSaved)) {
        const skillTags: SetTagInput[] = data.skills.map(tag => ({ tagId: +tag.id }));

        const saveSkillsResponse = await saveSkills({
          skills: skillTags
        });

        if (saveSkillsResponse.error) {
          return handleErrors(saveSkillsResponse.error, setError);
        }
      }

      if (checkIsDirty(data.hobbies, hobbiesSaved)) {
        const hobbyTags: SetTagInput[] = data.hobbies.map(tag => ({ tagId: +tag.id }));

        const saveHobbiesResponse = await saveHobbies({
          hobbies: hobbyTags
        });

        if (saveHobbiesResponse.error) {
          return handleErrors(saveHobbiesResponse.error, setError);
        }
      }

      setSavedHobbies(() => data.hobbies);
      setSavedSkills(() => data.skills);

      const updatedData = {
        ...user,
        tags: [...data.hobbies.map(item => ({ ...item, isHobby: true })), ...data.skills.map(item => ({ ...item, isHobby: false }))]
      } as EditUserNode;

      onUpdateUser(updatedData);
      setSaveBtnState('success');
    } catch (error) {
      console.error(error);
      handleToast('Oops, something went wrong', 'error');
      setSaveBtnState('idle');
    }
  }, [checkIsDirty, handleErrors, handleToast, hobbiesSaved, onUpdateUser, saveHobbies, saveSkills, setError, skillsSaved, user]);

  useEffect(() => {
    onSetDirtyForm(TabIds.SERVICES, isDirty);
  }, [isDirty, onSetDirtyForm]);

  useEffect(() => {
    if (currentTab !== TabIds.SERVICES) {
      onSetDirtyForm(TabIds.SERVICES, false);
    }
  }, [currentTab, onSetDirtyForm]);

  const formRef = useRef<HTMLFormElement>(null);
  const { handleFocus, handleBlur } = useAdditionalFormSpace(formRef);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)} ref={formRef}>
      <Controller
        control={control}
        name="skills"
        render={({ field: { value, onChange, onBlur, ...field } }) => (
          <div className="w-full bg-white flex flex-col rounded-radius-2xl gap-6 p-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="skills" className="font-medium text-small max-w-max flex text-gray-900 relative">
                If a company hired you as a consultant, what would you help them with?
              </label>
              <AsyncSelectCreatable
                inputId="skills"
                isSearchable
                cacheOptions
                defaultOptions={skills}
                getOptionLabel={(skill: Tag) => skill.name || (skill as unknown as ICreatableNewOption)?.label || ''}
                getOptionValue={(skill: Tag) => skill.id}
                className="w-full"
                controlShouldRenderValue={false}
                icon={<SearchIcon width={20} height={20} className='text-gray-500 ml-2' />}
                createOptionPosition="first"
                onCreateOption={async (inputValue: string) => {
                  const resp = await createSkill({ skill: { name: inputValue } });

                  if (resp.error) {
                    alert(`Error during creating skill: ${resp.error.message}`);
                    return;
                  }
                  if (!resp?.data?.createSkill?.skill) {
                    alert(`Error during creating skill`);
                    return;
                  }

                  const newItem: TagNode = resp.data.createSkill.skill;
                  let _skills = [...skillsWatch, newItem];
                  setValue('skills', _skills as Tag[], { shouldDirty: true });
                }}
                loadOptions={(inputValue, callback) => {
                  loadSkills({ search: inputValue })
                    .then(response => {
                      callback(response.data?.loadSkills?.skills ?? []);
                    })
                }}
                components={{
                  Control: ControlComponent,
                  Option: OptionWithoutSelectedComponent,
                  Menu: MenuComponent,
                  Placeholder: PlaceholderComponent,
                }}
                {...field}
                onFocus={handleFocus}
                onBlur={() => handleBlur(onBlur)}
                onChange={(newValue) => {
                  if (!newValue) {
                    setValue('skills', [], { shouldDirty: true });
                    return;
                  }
                  let _skills;
                  if (skillsWatch.find(item => item.id === (newValue as Tag)?.id)) {
                    _skills = skillsWatch.filter(item => item.id !== (newValue as Tag)?.id);
                  } else {
                    _skills = [...skillsWatch, newValue];
                  }
                  setValue('skills', _skills as Tag[], { shouldDirty: true });
                }}
                menuShouldScrollIntoView
              />
            </div>
            {!!skillsWatch.length && (
              <div className="flex flex-wrap gap-2">
                {skillsWatch.map(item => (
                  <div
                    className="px-lg py-sm bg-gray-100 rounded-full text-small text-black flex gap-1"
                    key={item.id}
                  >
                    {item.name}

                    <Button
                      size="x-small"
                      className="!p-0"
                      variant="nakedGrayLink"
                      onClick={(event) => {
                        event.stopPropagation();
                        const _skills = skillsWatch.filter(skill => skill.id !== item.id);

                        setValue('skills', _skills, { shouldDirty: true });
                      }}
                    >
                      <CloseIcon height={18} width={18} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      />

      <Controller
        control={control}
        name="hobbies"
        render={({ field: { value, onChange, onBlur, ...field } }) => (
          <div className="w-full bg-white flex flex-col rounded-radius-2xl gap-6 p-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="hobbies" className="font-medium text-small max-w-max flex text-gray-900 relative">
                What do you enjoy doing when you&apos;re not working?
              </label>
              <AsyncSelectCreatable
                inputId="hobbies"
                isSearchable
                cacheOptions
                defaultOptions={hobbies}
                icon={<SearchIcon width={20} height={20} className="text-gray-500 ml-2" />}
                getOptionLabel={(hobby: Tag) => hobby.name || (hobby as unknown as ICreatableNewOption)?.label || ''}
                getOptionValue={(hobby: Tag) => hobby.id}
                className="w-full"
                controlShouldRenderValue={false}
                createOptionPosition="first"
                onCreateOption={async (inputValue: string) => {
                  const resp = await createHobby({ hobby: { name: inputValue } });

                  if (resp.error) {
                    alert(`Error during creating hobby: ${resp.error.message}`);
                    return;
                  }
                  if (!resp?.data?.createHobby?.hobby) {
                    alert(`Error during creating hobby`);
                    return;
                  }

                  const newItem: TagNode = resp.data.createHobby.hobby;
                  let _hobbies = [...hobbiesWatch, newItem];
                  setValue('hobbies', _hobbies as Tag[], { shouldDirty: true });
                }}
                loadOptions={(inputValue, callback) => {
                  loadHobbies({ search: inputValue })
                    .then(response => {
                      callback(response.data?.loadHobbies?.hobbies ?? []);
                    })
                }}
                {...field}
                onFocus={handleFocus}
                onBlur={() => handleBlur(onBlur)}
                components={{
                  Control: ControlComponent,
                  Option: OptionWithoutSelectedComponent,
                  Menu: MenuComponent,
                  Placeholder: PlaceholderComponent,
                }}
                onChange={(newValue) => {
                  if (!newValue) {
                    setValue('hobbies', [], { shouldDirty: true });
                    return;
                  }
                  let _hobbies;
                  if (hobbiesWatch.find(item => item.id === (newValue as Tag)?.id)) {
                    _hobbies = hobbiesWatch.filter(item => item.id !== (newValue as Tag)?.id);
                  } else {
                    _hobbies = [...hobbiesWatch, newValue];
                  }
                  setValue('hobbies', _hobbies as Tag[], { shouldDirty: true });
                }}
                menuShouldScrollIntoView
              />
            </div>
            {!!hobbiesWatch.length && (
              <div className="flex flex-wrap gap-2">
                {hobbiesWatch.map(item => (
                  <div
                    className="px-xl py-sm bg-brand-300 rounded-full text-small text-brand-700 flex gap-[10px]"
                    key={item.id}
                  >
                    {item.name}

                    <Button
                      size="x-small"
                      className="!p-0"
                      variant="nakedBrandLink"
                      onClick={(event) => {
                        event.stopPropagation();
                        const _hobbies = hobbiesWatch.filter(hobby => hobby.id !== item.id);

                        setValue('hobbies', _hobbies, { shouldDirty: true });
                      }}
                    >
                      <CloseIcon height={18} width={18} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      />

      {!!errors.root?.message && (
        <span className="text-small text-error-600">{errors.root?.message}</span>
      )}

      <AnimatedButton
        disabled={!isDirty}
        variant="primaryBrand"
        size="medium"
        type="submit"
        state={saveBtnState}
        successText="Saved!"
        successDuration={1700}
      >
        Save
      </AnimatedButton>
    </form>
  );
};
